import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNativePushNotifications } from '@/hooks/useNativePushNotifications';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useReviewRequest } from '@/hooks/useReviewRequest';
import { supabase } from '@/integrations/supabase/client';
import { onMeaningfulAction, type MeaningfulAction } from '@/lib/meaningfulActions';
import {
  getEligibility, getPromptState, isQuiet, majorPromptAllowed, markMajorPrompt, updatePromptState,
  NOTIF_PREPROMPT_MAX, NOTIF_RETRY_MS,
} from '@/lib/promptCoordinator';
import { track } from '@/lib/analytics';
import { IS_NATIVE } from '@/hooks/useNativePlatform';
import NotificationPrePrompt from './NotificationPrePrompt';

/**
 * Coordinates the two "conversion moment" prompts:
 *  - Vent-branded notification pre-permission screen, shown after a meaningful
 *    action (never on first launch / straight after sign-in).
 *  - Store review requests (handled by useReviewRequest).
 */
export const PostLoginNotificationPrompt = () => {
  const { user } = useAuth();
  const location = useLocation();
  const native = useNativePushNotifications();
  const web = usePushNotifications();
  useReviewRequest();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const checking = useRef(false);
  const lastTrigger = useRef<MeaningfulAction>('wellness_logged');

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const alreadyEnabled = IS_NATIVE
    ? native.permission === 'granted' || native.permission === 'denied'
    : !web.isSupported || web.isSubscribed || web.permission !== 'default';

  const maybeShow = useCallback(async (trigger: MeaningfulAction) => {
    if (!user || checking.current || open || alreadyEnabled) return;
    if (IS_NATIVE && native.permission === 'unknown') return;
    // Never during onboarding/auth flows or right after an error/sign-in.
    if (/onboarding|auth|welcome|verify/i.test(pathRef.current) || isQuiet()) return;
    checking.current = true;
    try {
      const { data: profile } = await supabase.from('profiles').select('onboarding_completed').eq('user_id', user.id).maybeSingle();
      if (profile && profile.onboarding_completed === false) return;

      const state = await getPromptState(user.id);
      if (state.notif_os_decision === 'denied' || state.notif_os_decision === 'granted') return;
      if (state.notif_preprompt_count >= NOTIF_PREPROMPT_MAX) return;
      if (!majorPromptAllowed(state)) return;

      const elig = await getEligibility();
      if (state.notif_preprompt_count > 0) {
        const last = state.notif_preprompt_last_at ? new Date(state.notif_preprompt_last_at).getTime() : 0;
        if (Date.now() - last < NOTIF_RETRY_MS) return;
        if (!elig || elig.active_days - state.notif_preprompt_last_active_days < 2) return;
      }

      lastTrigger.current = trigger;
      await updatePromptState(user.id, {
        notif_preprompt_count: state.notif_preprompt_count + 1,
        notif_preprompt_last_at: new Date().toISOString(),
        notif_preprompt_last_active_days: elig?.active_days ?? 0,
      });
      track('notification_permission_preprompt_shown', {
        trigger_source: trigger,
        attempt: state.notif_preprompt_count + 1,
        account_age_days: elig ? Math.floor(elig.account_age_days) : undefined,
        active_days: elig?.active_days,
        meaningful_action_count: elig?.meaningful_action_count,
      });
      // Let the triggering action (toast, navigation) settle first.
      setTimeout(() => setOpen(true), 1200);
    } catch (e) {
      console.warn('[PrePrompt] check failed', e);
    } finally {
      checking.current = false;
    }
  }, [user, open, alreadyEnabled, native.permission]);

  useEffect(() => onMeaningfulAction((a) => { void maybeShow(a); }), [maybeShow]);

  const handleLater = () => {
    setOpen(false);
    track('notification_permission_preprompt_later', { trigger_source: lastTrigger.current });
  };

  const handleAccept = async () => {
    if (!user) return;
    setBusy(true);
    track('notification_permission_preprompt_accept', { trigger_source: lastTrigger.current });
    let granted = false;
    try {
      if (IS_NATIVE) {
        await native.register();
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const res = await PushNotifications.checkPermissions();
        granted = res.receive === 'granted';
      } else {
        await web.subscribe();
        granted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
      }
    } catch (e) {
      console.warn('[PrePrompt] permission request failed', e);
    }
    track(granted ? 'notification_permission_granted' : 'notification_permission_denied', { trigger_source: lastTrigger.current });
    await markMajorPrompt(user.id, { notif_os_decision: granted ? 'granted' : 'denied' });
    setBusy(false);
    setOpen(false);
  };

  return <NotificationPrePrompt open={open} busy={busy} onAccept={handleAccept} onLater={handleLater} />;
};
